import { apiClient } from './client';

/**
 * Per-participant baseline captured by the capture-app — ROADMAP
 * #15. Re-used across many sessions until it expires or the
 * participant is re-baselined. Stored in the tenant's schema
 * (`tenant_<slug>.baselines`) since ROADMAP #8 PR D.
 */
export interface Baseline {
  id: string;
  participant_user_id: string;
  participant_label: string | null;
  device_id: string | null;
  device_label: string | null;
  duration_s: number;
  /** ISO timestamps. */
  captured_at: string;
  created_at: string;
  /**
   * Server-computed from `captured_at + settings.baseline_expiry_days`.
   * Surfaced in the response so the UI can show "Expires in N days"
   * without re-implementing the threshold on the client side.
   */
  expires_at: string;
  /**
   * Server-evaluated `expires_at < now()`. Stale by the time it
   * arrives if the client clock has drifted, but treating it as
   * authoritative-on-receipt is the simpler contract.
   */
  is_expired: boolean;
  /** Computed metrics blob (trait scores etc). Server-side JSONB. */
  metrics_json: Record<string, unknown> | null;
  /**
   * S3 object key for the baseline's NDJSON recording. Null when the
   * kiosk operator skipped the Export step. The portal's NDJSON
   * download icon gates on this; the JSON-row dump stays available
   * either way.
   */
  s3_key: string | null;
  /**
   * Non-null when the baseline is soft-deleted (disabled). The "Show
   * disabled" toggle surfaces it for Restore.
   */
  deleted_at: string | null;
  /**
   * Originating tenant — populated on the cross-tenant `/baselines/me`
   * feed (ROADMAP #54) so the participant Dashboard can filter by
   * tenant. Null on tenant-scoped / device-facing responses.
   */
  tenant_id?: string | null;
  tenant_slug?: string | null;
  tenant_name?: string | null;
}

export interface BaselineDownload {
  url: string;
  filename: string;
  expires_in_seconds: number;
}

/**
 * Tenant_admin view: every baseline in this tenant, newest first.
 * Capped at 200 rows server-side until a pager lands.
 */
export async function listBaselinesForTenant(
  tenantId: string,
  includeDeleted = false,
): Promise<Baseline[]> {
  const { data } = await apiClient.get<Baseline[]>(
    `/api/v1/tenants/${tenantId}/baselines`,
    { params: includeDeleted ? { include_deleted: true } : undefined },
  );
  return data;
}

/** Soft-delete (disable) a baseline. */
export async function disableBaseline(
  tenantId: string,
  baselineId: string,
): Promise<void> {
  await apiClient.delete(
    `/api/v1/tenants/${tenantId}/baselines/${baselineId}`,
  );
}

/** Undo a soft-delete — bring a disabled baseline back. */
export async function restoreBaseline(
  tenantId: string,
  baselineId: string,
): Promise<void> {
  await apiClient.post(
    `/api/v1/tenants/${tenantId}/baselines/${baselineId}/restore`,
  );
}

/**
 * Participant cross-tenant view: the current user's baselines
 * across every tenant they participate in. Same iteration pattern
 * as `listMyCaptureSessions`.
 */
export async function listMyBaselines(): Promise<Baseline[]> {
  const { data } = await apiClient.get<Baseline[]>('/api/v1/baselines/me');
  return data;
}

/**
 * Request a short-lived presigned URL for a baseline's NDJSON
 * recording. Mirror of getCaptureSessionDownload(); the portal
 * navigates the browser to `data.url` to trigger the download.
 */
export async function getBaselineDownload(
  tenantId: string,
  baselineId: string,
): Promise<BaselineDownload> {
  const { data } = await apiClient.get<BaselineDownload>(
    `/api/v1/tenants/${tenantId}/baselines/${baselineId}/download`,
  );
  return data;
}
